import React from 'react';
import { useQuery } from 'react-apollo';
import { useLocation, useHistory } from 'react-router';
import qs from 'qs';
import loadable from '@loadable/component';
import t from '@jetshop/intl';
import PaginationContext from '@jetshop/core/components/Pagination/PaginationContext';
import { PaginationBehaviour } from '@jetshop/ui/Pagination';
import useFindify from '@findify/react-bundle';
import { styled } from 'linaria/react';

import LoadingPage from '../LoadingPage';
import MaxWidth from '../Layout/MaxWidth';
import CategoryHeader from './CategoryHeader';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import CategoryPageFallbackQuery from './CategoryPageFallbackQuery.gql';
import StandardCategoryPage from './StandardCategoryPage';
import Spinner from '../../components/ui/Spinner';
import { Query } from 'react-apollo';
import { FindifyRecommendations, FindifyTracker } from '../Findify';
import SubcategoriesButtons from './SubcategoriesButtons';
import PostText from './PostText';
import ProductGrid from './ProductGrid';
import Filters from './Filters';
import { Pagination } from '../Pagination/Pagination';
import { theme } from '../Theme';
import MetaTags from '../MetaTags';
import Hreflang from '../Hreflang';
import striptags from 'striptags';

export const LoadableStandardCategoryPage = loadable(
  () => import('./StandardCategoryPage'),
  {
    fallback: <LoadingPage />
  }
);

export const LoadableWindowedCategoryPage = loadable(
  () => import('./WindowedCategoryPage'),
  {
    fallback: <LoadingPage />
  }
);

const Container = styled(MaxWidth)`
  padding: 0 0.75rem;
`;

const FallbackContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin: 75px auto 0;

  .grid {
    width: 100%;
    max-width: 1100px;
  }

  ${theme.above.sm} {
    flex-direction: row;

    .filters {
      min-width: 190px;
    }

    .grid {
      padding-left: 40px;
    }
  }

  ${theme.above.md} {
    .filters {
      padding-left: 40px;
    }
  }
`;

const FindifyCollection = ({ children, fallback }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const [container, isReady, hasError] = useFindify({
    type: 'smart-collection',
    history,
    config: {
      slot: pathname.replace('/', '').toLowerCase()
    }
  });

  return (
    <>
      <div ref={container} />
      {hasError ? fallback() : !isReady && children}
    </>
  );
};

const CategoryPage = props => {
  const category = props.category;
  const result = props.result;

  if (!category) {
    return null;
  }

  const { hasSubcategories, subcategories } = category;

  const headDescription =
    category.head.metaTags.find(({ name }) => name === 'description')
      ?.content || '';
  const headKeywords =
    category.head.metaTags.find(({ name }) => name === 'keywords')?.content ||
    '';
  const categoryImageUrl =
    category.images?.length > 0 ? category.images[0].url : null;

  const { alternateRoutes } = result?.data?.route || {};

  return (
    <>
      {category ? (
        <>
          <MetaTags
            title={category.head.title || category.name}
            canonicalPath={category.primaryRoute.canonicalPath}
            description={
              headDescription
                ? striptags(headDescription)
                : striptags(category.content)
            }
            keywords={headKeywords}
            imageUrl={categoryImageUrl}
          />
          <Hreflang alternateRoutes={alternateRoutes} />
          <CategoryHeader
            category={category}
            parents={result?.data?.route?.parents ?? null}
          />
        </>
      ) : (
        <CategoryHeaderLoadingState />
      )}
      {hasSubcategories && (
        <SubcategoriesButtons
          hasSubcategories={hasSubcategories}
          subcategories={subcategories}
        />
      )}
      <Container>
        <FindifyTracker />

        <FindifyCollection
          fallback={() => (
            <FallbackProducts id={category.id} path={result.data.route} />
          )}
        >
          <Spinner />
        </FindifyCollection>

        <FindifyRecommendations recIdString={'home-findify-rec-3'} />
        <FindifyRecommendations recIdString={'category-findify-rec-4'} />

        <PostText category={category} />
      </Container>
    </>
  );
};

function FallbackProducts({ id, path }) {
  return (
    <PaginationContext.Consumer>
      {({ currentPage, goToPage, perPage }) => {
        const offset = (currentPage - 1) * perPage;
        return (
          <Query
            query={CategoryPageFallbackQuery}
            variables={{ id, first: perPage, offset }}
          >
            {({ loading, data }) =>
              !loading && data ? (
                <FallbackContainer>
                  <div className="filters">
                    <Filters
                      filters={data.category.products?.filters || []}
                      sortOrders={data.category.products?.sortOrders || []}
                      result={data.category.products?.result}
                    />
                  </div>
                  <div className="grid">
                    <PaginationBehaviour
                      total={data.category.products?.totalResults}
                      currentPage={currentPage}
                      goToPage={goToPage}
                      perPage={perPage}
                    >
                      {props => (
                        <>
                          <ProductGrid
                            products={data.category.products.result}
                            listName={data.category.name}
                            categoryPath={data.category.isDynamic ? null : path}
                            loading={false}
                            imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
                          />
                          <Pagination {...props} />
                        </>
                      )}
                    </PaginationBehaviour>
                  </div>
                </FallbackContainer>
              ) : null
            }
          </Query>
        );
      }}
    </PaginationContext.Consumer>
  );
}

export default CategoryPage;
